import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LangToggle from "./LangToggle";
import ThemeToggle from "./ThemeToggle";

type Props = {
  children: ReactNode;
  /** Page-specific buttons, placed before the language and theme controls. */
  actions?: ReactNode;
  /** `narrow` for single-card pages, `center` to also centre vertically. */
  width?: "wide" | "narrow" | "center";
  homeHref?: string;
  /** Small line under the wordmark, e.g. the current game code. */
  tagline?: ReactNode;
};

/* The file name has a space in it, so the URL is encoded. */
const LOGO_SRC = `${import.meta.env.BASE_URL}Elguards%20Logo.png`;

function BrandMark() {
  /* Decorative: the link already carries the app name as its text. The
     width and height match the file (515x768) so the bar never shifts while
     the image loads. */
  return (
    <img
      className="brand__logo"
      src={LOGO_SRC}
      alt=""
      width={23}
      height={34}
      decoding="async"
    />
  );
}

export default function AppShell({
  children,
  actions,
  width = "wide",
  homeHref = "/",
  tagline,
}: Props) {
  const { t } = useTranslation();

  const mainClass = [
    "shell__main",
    width !== "wide" ? "shell__main--narrow" : "",
    width === "center" ? "shell__main--center" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t("ui.skipToContent")}
      </a>

      <header className="shell__header">
        <div className="shell__bar">
          <Link to={homeHref} className="brand">
            <BrandMark />
            <span>
              <span className="brand__name">{t("app.title")}</span>
              <span className="brand__sub">{tagline ?? t("ui.brandSub")}</span>
            </span>
          </Link>

          <div className="shell__actions">
            {actions}
            <LangToggle />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main id="main" className={mainClass}>
        {children}
      </main>
    </div>
  );
}
